/**
 * Rounds on the website (`EX-DIV06`, `docs/29` §8.1, ADR-0126, Wave 29
 * decision 5): 2 to 4 division contests in one window, each a whole contest
 * with its own slug, board, rating and certificates.
 *
 * Every answer about the reader is the server's `viewer` on the round read:
 * the division their rating sends them to (`home`), the place they hold
 * (`entry`), whether they may register now and whether they are staff. This
 * module only decides which of those a division's card shows, so the card,
 * its test and the capture agree on one rule.
 */
import type { ContestDivision, ContestPhase, PublicRoundDivision, PublicRoundViewer } from "../types";

/** The round page's path. Contests live under `/contests/<slug>`, so no contest slug can take it. */
export function roundHref(locale: string, slug: string): string {
  return `/${locale}/rounds/${slug}`;
}

/**
 * Where a link to a contest goes: the round page when the contest leads a
 * round of divisions (the directory's one row for it, the next-round cards),
 * else the contest's own page. A Div. 2 reader must never land on Div. 1.
 */
export function contestOrRoundHref(locale: string, item: { slug: string; round?: { slug: string } | null }): string {
  return item.round ? roundHref(locale, item.round.slug) : `/${locale}/contests/${item.slug}`;
}

/** Inside the window: the division takes submissions (a frozen board still does). */
export function isLivePhase(phase: ContestPhase): boolean {
  return phase === "running" || phase === "frozen";
}

/** Over: the board is final, or waiting for the unfreeze. */
export function isEndedPhase(phase: ContestPhase): boolean {
  return phase === "ended" || phase === "unfrozen";
}

/**
 * The round's phase, read off its first division: the round owns one window,
 * so every division shares the clock. A division a moderator unfroze sooner
 * still reads as over, which is all the head needs.
 */
export function roundPhase(divisions: readonly Pick<PublicRoundDivision, "phase">[]): ContestPhase {
  return divisions[0]?.phase ?? "scheduled";
}

/**
 * Which edge of a division's band keeps a rating out, for "your rating 1452 is
 * below 2000" and "1724 is 1600 or more". `null` when the rating is inside, or
 * the division has no band (a round's divisions always have one).
 */
export function outsideEdge(
  division: Pick<ContestDivision, "ratedFrom" | "ratedBelow"> | null,
  rating: number,
): { side: "below" | "above"; edge: number } | null {
  if (!division) return null;
  if (division.ratedFrom !== null && rating < division.ratedFrom) return { side: "below", edge: division.ratedFrom };
  if (division.ratedBelow !== null && rating >= division.ratedBelow) return { side: "above", edge: division.ratedBelow };
  return null;
}

/**
 * What one division's card says to this reader, in the order the server's
 * own door asks (`docs/29` §8.1): staff of any division compete in none; the
 * place someone holds is the one division they are in; their rating's home is
 * the one they may register for; every other division is closed to them, with
 * the edge of its band that keeps them out.
 */
export type RoundCardState =
  | { kind: "signedOut" }
  | { kind: "staff"; role: NonNullable<PublicRoundViewer["staffRole"]> }
  | { kind: "entry"; entry: NonNullable<PublicRoundViewer["entry"]> }
  | { kind: "elsewhere"; entrySlug: string }
  | { kind: "home"; canRegister: boolean }
  | { kind: "other"; outside: { side: "below" | "above"; edge: number } | null };

export function roundCardState(division: PublicRoundDivision, viewer: PublicRoundViewer | null): RoundCardState {
  if (!viewer) return { kind: "signedOut" };
  if (viewer.staffRole) return { kind: "staff", role: viewer.staffRole };
  if (viewer.entry) {
    return viewer.entry.slug === division.slug
      ? { kind: "entry", entry: viewer.entry }
      : { kind: "elsewhere", entrySlug: viewer.entry.slug };
  }
  if (viewer.home === division.slug) return { kind: "home", canRegister: viewer.canRegister };
  return { kind: "other", outside: outsideEdge(division.division, viewer.rating) };
}

/** A division as a sentence names it: its label ("Div. 2"), or its title when it has none. */
export function roundDivisionName(division: Pick<PublicRoundDivision, "division" | "title_en" | "title_bn">, locale: string): string {
  return division.division?.label ?? ((locale === "bn" ? division.title_bn : null) ?? division.title_en);
}
